// Tab Drag and Drop Utilities
// Architecture: v4.0.0 Multiple Charts Implementation
// Provides drag and drop functionality for chart tabs reordering

import React, { useState, useRef, useCallback } from 'react';
import { Box } from '@chakra-ui/react';

/**
 * Drag and Drop State Interface
 */
export interface DragState {
  isDragging: boolean;
  draggedIndex: number | null;
  dragOverIndex: number | null;
  dragStartX: number;
  dragStartY: number;
}

/**
 * Drag and Drop Event Handlers
 */
export interface DragHandlers {
  onDragStart: (index: number, event: React.DragEvent) => void;
  onDragOver: (index: number, event: React.DragEvent) => void;
  onDragEnd: (event: React.DragEvent) => void;
  onDrop: (fromIndex: number, toIndex: number) => void;
}

/**
 * Use Drag and Drop Hook
 * Custom hook for managing tab drag and drop state
 */
export function useTabDragAndDrop(
  onReorder: (fromIndex: number, toIndex: number) => void
): {
  dragState: DragState;
  dragHandlers: DragHandlers;
  isDraggedTab: (index: number) => boolean;
  isDragOverTab: (index: number) => boolean;
} {
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedIndex: null,
    dragOverIndex: null,
    dragStartX: 0,
    dragStartY: 0
  });

  /**
   * Handle drag start
   */
  const handleDragStart = useCallback((index: number, event: React.DragEvent) => {
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const startX = event.clientX - rect.left;
    const startY = event.clientY - rect.top;
    
    setDragState({
      isDragging: true,
      draggedIndex: index,
      dragOverIndex: null,
      dragStartX: startX,
      dragStartY: startY
    });

    // Set drag data
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', index.toString());
    
    // Set custom drag image (optional)
    const dragImage = event.target as HTMLElement;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx && dragImage) {
      canvas.width = dragImage.offsetWidth;
      canvas.height = dragImage.offsetHeight;
      ctx.fillStyle = 'rgba(59, 130, 246, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#3B82F6';
      ctx.strokeRect(0, 0, canvas.width, canvas.height);
      // Use the fresh coordinates instead of stale state
      event.dataTransfer.setDragImage(canvas, startX, startY);
    }
  }, []); // Remove stale dependencies

  /**
   * Handle drag over
   */
  const handleDragOver = useCallback((index: number, event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    
    if (dragState.draggedIndex !== null && dragState.draggedIndex !== index) {
      setDragState(prev => ({
        ...prev,
        dragOverIndex: index
      }));
    }
  }, [dragState.draggedIndex]);

  /**
   * Handle drag end
   */
  const handleDragEnd = useCallback((event: React.DragEvent) => {
    const fromIndex = dragState.draggedIndex;
    const toIndex = dragState.dragOverIndex;
    
    // Reset drag state
    setDragState({
      isDragging: false,
      draggedIndex: null,
      dragOverIndex: null,
      dragStartX: 0,
      dragStartY: 0
    });

    // Call reorder if valid drop
    if (fromIndex !== null && toIndex !== null && fromIndex !== toIndex) {
      onReorder(fromIndex, toIndex);
    }
  }, [dragState.draggedIndex, dragState.dragOverIndex, onReorder]);

  /**
   * Handle drop (for better browser compatibility)
   */
  const handleDrop = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex !== toIndex) {
      onReorder(fromIndex, toIndex);
    }
  }, [onReorder]);

  /**
   * Check if tab is being dragged
   */
  const isDraggedTab = useCallback((index: number) => {
    return dragState.draggedIndex === index;
  }, [dragState.draggedIndex]);

  /**
   * Check if tab is drag over target
   */
  const isDragOverTab = useCallback((index: number) => {
    return dragState.dragOverIndex === index && dragState.draggedIndex !== index;
  }, [dragState.dragOverIndex, dragState.draggedIndex]);

  return {
    dragState,
    dragHandlers: {
      onDragStart: handleDragStart,
      onDragOver: handleDragOver,
      onDragEnd: handleDragEnd,
      onDrop: handleDrop
    },
    isDraggedTab,
    isDragOverTab
  };
}

/**
 * Draggable Tab Wrapper Component
 * Wraps tab component with drag and drop functionality
 */
export interface DraggableTabProps {
  index: number;
  isDragging: boolean;
  isDragOver: boolean;
  dragHandlers: DragHandlers;
  children: React.ReactNode;
  disabled?: boolean;
}

export const DraggableTab: React.FC<DraggableTabProps> = ({
  index,
  isDragging,
  isDragOver,
  dragHandlers,
  children,
  disabled = false
}) => {
  const tabRef = useRef<HTMLDivElement>(null);

  return (
    <Box
      ref={tabRef}
      draggable={!disabled}
      onDragStart={(e) => !disabled && dragHandlers.onDragStart(index, e)}
      onDragOver={(e) => !disabled && dragHandlers.onDragOver(index, e)}
      onDragEnd={(e) => !disabled && dragHandlers.onDragEnd(e)}
      onDrop={() => !disabled && dragHandlers.onDrop(dragHandlers.onDragStart.length, index)}
      cursor={disabled ? 'default' : isDragging ? 'grabbing' : 'grab'}
      opacity={isDragging ? 0.5 : 1}
      transform={isDragging ? 'rotate(2deg)' : 'none'}
      borderLeft={isDragOver ? '3px solid' : 'none'}
      borderColor={isDragOver ? 'blue.400' : 'transparent'}
      transition="all 0.2s ease"
      position="relative"
      _hover={{
        transform: disabled ? 'none' : 'translateY(-1px)',
        boxShadow: disabled ? 'none' : 'sm'
      }}
    >
      {children}
      
      {/* Drag indicator */}
      {isDragging && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="blue.100"
          borderRadius="md"
          border="2px dashed"
          borderColor="blue.300"
          opacity={0.8}
          pointerEvents="none"
        />
      )}
      
      {/* Drop zone indicator */}
      {isDragOver && (
        <Box
          position="absolute"
          top="50%"
          left={-2}
          width="4px"
          height="60%"
          bg="blue.400"
          borderRadius="2px"
          transform="translateY(-50%)"
          animation="pulse 1s infinite"
        />
      )}
    </Box>
  );
};

/**
 * Drag Preview Component
 * Shows preview of tab being dragged
 */
export interface DragPreviewProps {
  title: string;
  isActive: boolean;
  isDragging: boolean;
}

export const DragPreview: React.FC<DragPreviewProps> = ({
  title,
  isActive,
  isDragging
}) => {
  if (!isDragging) return null;

  return (
    <Box
      position="fixed"
      top={-1000} // Hidden off-screen, used only for drag image
      left={-1000}
      pointerEvents="none"
      bg={isActive ? 'blue.100' : 'gray.100'}
      borderRadius="md"
      border="2px solid"
      borderColor={isActive ? 'blue.300' : 'gray.300'}
      px={3}
      py={1}
      fontSize="sm"
      fontWeight={isActive ? 'semibold' : 'normal'}
      color={isActive ? 'blue.700' : 'gray.700'}
      boxShadow="lg"
      transform="rotate(-2deg)"
    >
      {title}
    </Box>
  );
};

export default {
  useTabDragAndDrop,
  DraggableTab,
  DragPreview
};